import Box from "@mui/material/Box";
import {Stack} from "@mui/material";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationReservationListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {
    ReservationAggregationService
} from "crqlar-react-common/dist/services/aggregation/reservationAggregation.service";
import type {ReservationListRequest} from "crqlar-react-common/dist/models/reservation/reservationList.request";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useReservationFilterOptions} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableExport,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    serverTableExportLimit,
    type ServerTableExportQuery,
    type ServerTableUrlStateOptions,
    useServerTable
} from "../../common/serverTable";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {NotesElement} from "../../common/uiElements/NotesElement";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import {AliasAgeCategoriesElement} from "../../common/uiElements/AliasAgeCategoriesElement";
import SettingsListPage from "../layout/SettingsListPage";
import {AggregationResourceElement} from "../../common/uiElements/ResourceElement";

const RESERVATIONS_URL_STATE: ServerTableUrlStateOptions = {
    filterAliases: [{
        queryParam: 'profileId',
        field: 'profileIds',
        operator: DataTypeOperator.IN
    }]
};

const ReservationsList = () => {
    useTrackScreenVisit('system reservations');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const filterOptionsQuery = useReservationFilterOptions();
    const ageCategories = useAllAgeCategories();

    const columns = useMemo<ColumnDef<AggregationReservationListItem>[]>(() => [
        {
            id: 'profile',
            header: t('Profile'),
            accessorFn: reservation => reservation.profileName,
            cell: ({row}) => row.original.profileName || '-',
            size: 240,
            meta: {defaultActive: true, grow: true, printable: true, printDefault: true}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: reservation => reservation.status,
            cell: ({row}) => <ReservationStatusElement status={row.original.status}/>,
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'status',
                printable: true,
                printDefault: true,
                printCell: row => <ReservationStatusElement status={row.original.status} asPrint/>
            }
        },
        {
            id: 'start',
            header: t('Start'),
            accessorFn: reservation => reservation.startDate,
            cell: ({row}) => row.original.startDate ? formatFullDateTime(row.original.startDate) : '',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'expectedStartDate',
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.startDate)
            }
        },
        {
            id: 'end',
            header: t('End'),
            accessorFn: reservation => reservation.endDate,
            cell: ({row}) => row.original.endDate ? formatFullDateTime(row.original.endDate) : '',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'expectedEndDate',
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.endDate)
            }
        },
        {
            id: 'service',
            header: t('Service'),
            accessorFn: reservation => reservation.service?.name,
            cell: ({row}) => row.original.service?.name ?? '',
            size: 220,
            meta: {defaultActive: true, printable: true, printDefault: true}
        },
        {
            id: 'resources',
            header: t('Resources'),
            accessorFn: reservation => reservation.resources.map(resource => resource.name).filter(Boolean),
            cell: ({row}) => (
                <Stack direction={'row'} gap={0.5} flexWrap={'wrap'}>
                    {row.original.resources.map(resource => (
                        <AggregationResourceElement key={resource.id} resource={resource}/>
                    ))}
                </Stack>
            ),
            size: 240,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'guestCount',
            header: t('Guest count abbreviation'),
            accessorFn: reservation => reservation.guestCount,
            cell: ({getValue}) => <Box>{getValue<number>()}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'guests',
            header: t('Age categories'),
            accessorFn: reservation => reservation.guests.adults + reservation.guests.children,
            cell: ({row}) => (
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    computedAgeCategories={row.original.guests}
                />
            ),
            size: 150,
            meta: {
                defaultActive: true,
                printable: true,
                printCell: row => (
                    <AgeCategoriesElement
                        ageCategories={ageCategories}
                        computedAgeCategories={row.original.guests}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'aliasAgeCategories',
            header: `${t('Age categories')} (${t('Alias')})`,
            accessorFn: reservation => reservation.aliasAgeCategories,
            cell: ({row}) => (
                <AliasAgeCategoriesElement
                    aliasAgeCategories={row.original.aliasAgeCategories}
                    ageCategories={ageCategories}
                />
            ),
            size: 150,
            meta: {
                defaultActive: false,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AliasAgeCategoriesElement
                        aliasAgeCategories={row.original.aliasAgeCategories}
                        ageCategories={ageCategories}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'source',
            header: t('Source'),
            accessorFn: reservation => reservation.fromBookingEngine,
            cell: ({row}) => (
                <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                    {row.original.fromBookingEngine && (
                        <BookOnlineIcon sx={{fontSize: 15, color: 'text.secondary'}}/>
                    )}
                    {t(row.original.fromBookingEngine ? 'Online Reservation' : 'Staff')}
                </Stack>
            ),
            size: 190,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: 'fromBookingEngine',
                printable: true,
                printDefault: true,
                printCell: row => t(row.original.fromBookingEngine ? 'Online Reservation' : 'Staff')
            }
        },
        {
            id: 'insertDate',
            header: t('Created'),
            accessorFn: reservation => reservation.insertDate,
            cell: ({row}) => row.original.insertDate ? formatFullDateTime(row.original.insertDate) : '',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.insertDate)
            }
        },
        {
            id: 'code',
            header: t('Code'),
            accessorFn: reservation => reservation.code,
            cell: ({row}) => row.original.code ?? '',
            size: 130,
            meta: {defaultActive: false, isServerSortable: true, serverSortField: 'code'}
        },
        {
            id: 'sector',
            header: t('Sector'),
            accessorFn: reservation => reservation.sector?.name,
            cell: ({row}) => row.original.sector?.name ?? '',
            size: 140,
            meta: {defaultActive: false}
        },
        {
            id: 'notes',
            header: t('Notes'),
            accessorFn: reservation => reservation.notes,
            cell: ({row}) => <NotesElement notes={row.original.notes}/>,
            size: 260,
            meta: {
                defaultActive: false,
                printable: true
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: reservation => reservation.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: reservation => reservation.updateDate,
            cell: ({row}) => row.original.updateDate ? formatFullDateTime(row.original.updateDate) : '',
            size: 180,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => formatFullDateTime(row.original.updateDate)
            }
        }
    ], [ageCategories, t]);

    const controller = useServerTable<AggregationReservationListItem>({
        tableId: 'system-reservations',
        columns,
        getRowId: reservation => reservation.id,
        onRowClick: reservation => navigate(`/system/reservations/${reservation.id}`),
        urlState: RESERVATIONS_URL_STATE,
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => ReservationAggregationService.listReservations({
            query: searchQuery || undefined,
            advancedFilter: filterRules.length ? {
                filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
            } : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery
    ): ReservationListRequest => ({
        query: searchQuery || undefined,
        advancedFilter: filterRules.length ? {
            filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
        } : undefined,
        limit: serverTableExportLimit(sorting)
    }), []);

    return (
        <SettingsListPage title={t('Reservations')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch placeholder={t('Search reservations')}/>
                        <ServerTableSort/>
                        <ServerTableFilter
                            options={filterOptionsQuery.data ?? []}
                            disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                        />
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Reservations')}/>
                        <ServerTableExport list="reservations" title={t('Reservations')} buildRequest={exportRequest}/>

                        <ServerTableCreate
                            label={t('Create reservation')}
                            onClick={() => navigate('/system/reservations/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ReservationsList;
